"use client";

import * as React from "react";
import { type ReactNode } from "react";
import {
  ProgressBar,
  type LabelProps,
} from "@progress/kendo-react-progressbars";

export interface GxLineStepperStep {
  id: string;
  label: string;
  content?: ReactNode;
}

export type StepCounterFormat = "default" | "slash" | "short";

export interface GxLineStepperProps {
  steps: GxLineStepperStep[];
  currentStep: number;

  /* UI */
  title?: string;
  showTitle?: boolean;
  showStepCounter?: boolean;

  /* Progress bar customization */
  barHeight?: number;
  barRadius?: number;

  /* Counter */
  counterFormat?: StepCounterFormat;

  /* Styling */
  className?: string;
  progressClassName?: string;
}

export const GxLineStepper = React.forwardRef<
  HTMLDivElement,
  GxLineStepperProps
>(
  (
    {
      steps,
      currentStep,
      title = "Setup",
      showTitle = false,
      showStepCounter = true,
      barHeight = 4,
      barRadius = 999,
      progressClassName = "",
      className = "",
    },
    ref,
  ) => {
    const progressValue = ((currentStep + 1) / steps.length) * 100;

    const EmptyLabel: React.FC<LabelProps> = () => null;

    return (
      <div
        ref={ref}
        className={`gx-line-stepper ${className}`}
        style={
          {
            "--gx-progress-height": `${barHeight}px`,
            "--gx-progress-radius": `${barRadius}px`,
          } as React.CSSProperties
        }
      >
        <div className="gx-line-stepper-header">
          <div className="gx-line-stepper-progress-wrapper">
            <ProgressBar
              value={progressValue}
              min={0}
              max={100}
              animation
              className={`gx-line-stepper-progress ${progressClassName}`}
              label={EmptyLabel}
            />
          </div>

          {showStepCounter && (
            <span className="gx-line-stepper-counter gx-p12-b ">
              <span className="gx-stepper-current-step">
                {Math.min(currentStep + 1, steps.length)}
              </span>{" "}
              of {steps.length} steps
            </span>
          )}
        </div>

        {showTitle && title && (
          <div className="gx-line-stepper-title">{title}</div>
        )}
      </div>
    );
  },
);

GxLineStepper.displayName = "GxLineStepper";
